import React, { useCallback, useEffect, useMemo, useState } from "react";
import useOverlayDismiss from "../../Elements/useOverlayDismiss";
import apiFetch from "../../../context/api";
import { formatActivityDetails } from "./activityDetails";

/**
 * Drill-down de una fila de Actividad, al estilo del modal de Contabilidad: baja al
 * registro que hay DEBAJO del hecho registrado, en vez de reimprimir el mismo hecho.
 *
 * El log ya guarda `entity_type` + `entity_id`; eso es lo que permite bajar:
 *   - transaction (una venta)  → sus LÍNEAS: qué se vendió, a quién y a cuánto.
 *   - product (movimientos de stock) → los últimos movimientos de ese producto.
 *   - user (altas / ediciones) → no hay nivel inferior: solo los pares del propio log.
 */

const money = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const qty = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 3 });

const PAYMENT_METHODS = { cash: "Efectivo", card: "Tarjeta", credit: "Crédito", transfer: "Transferencia" };

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/** Cabecera común: qué pasó, quién y cuándo. */
function Meta({ item }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-slate-400">Cuándo</dt>
      <dd className="text-slate-700">{fmtWhen(item.created_at)}</dd>
      <dt className="text-slate-400">Quién</dt>
      <dd className="text-slate-700">
        {item.actor_email || "—"}
        {item.actor_role ? <span className="text-slate-400"> · {item.actor_role}</span> : null}
      </dd>
    </dl>
  );
}

/** Detalle de una VENTA: sus líneas y el cuadre de importes. */
function SaleDetail({ sale }) {
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-slate-400">Socio</dt>
        <dd className="text-slate-700">
          {sale.member_name || "—"}
          {sale.member_number ? <span className="text-slate-400"> · nº {sale.member_number}</span> : null}
        </dd>
        <dt className="text-slate-400">Cobro</dt>
        <dd className="text-slate-700">{PAYMENT_METHODS[sale.payment_method] || sale.payment_method || "—"}</dd>
        {sale.processed_by && (
          <>
            <dt className="text-slate-400">Atendió</dt>
            <dd className="text-slate-700">{sale.processed_by}</dd>
          </>
        )}
      </dl>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 text-left">Producto</th>
            <th className="py-2 text-right">Cantidad</th>
            <th className="py-2 text-right">Precio</th>
            <th className="py-2 text-right">Importe</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sale.lines.length === 0 && (
            <tr><td colSpan={4} className="py-6 text-center text-slate-400">Esta venta no tiene líneas.</td></tr>
          )}
          {sale.lines.map((l, i) => (
            <tr key={i}>
              <td className="py-2 pr-2 font-medium text-slate-800">{l.product_name}</td>
              <td className="py-2 text-right text-slate-700">
                {qty(l.quantity)}
                {/* Lo cobrado y lo que salió de la estantería pueden diferir: la merma. */}
                {l.real_weight != null && Number(l.real_weight) !== Number(l.quantity) && (
                  <div className="text-[11px] text-slate-400">báscula: {qty(l.real_weight)}</div>
                )}
              </td>
              <td className="py-2 text-right text-slate-700">{money(l.unit_price)}</td>
              <td className="py-2 text-right text-slate-700">{money(l.line_subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* El precio de línea es el ORIGINAL: el descuento se aplicó al total de la venta
          y nunca se guardó línea a línea. Por eso se resta aquí y no ahí arriba. */}
      <dl className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">Subtotal</dt>
          <dd className="text-slate-700">{money(sale.subtotal)}</dd>
        </div>
        {Number(sale.discount) > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-500">Descuento</dt>
            <dd className="text-emerald-700">−{money(sale.discount)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
          <dt className="text-slate-800">Total cobrado</dt>
          <dd className="text-slate-900">{money(sale.total)}</dd>
        </div>
      </dl>
    </>
  );
}

/** Últimos movimientos del producto afectado. */
function ProductHistory({ history }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <th className="py-2 text-left">Movimiento</th>
          <th className="py-2 text-right">Cantidad</th>
          <th className="py-2 text-right">Saldo</th>
          <th className="py-2 text-right">Fecha</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {history.movements.length === 0 && (
          <tr><td colSpan={4} className="py-6 text-center text-slate-400">Sin movimientos.</td></tr>
        )}
        {history.movements.map((m, i) => (
          <tr key={i}>
            <td className="py-2 pr-2">
              <span className="font-medium text-slate-800">{m.movement_type}</span>
              {m.concept && <div className="text-[11px] text-slate-400">{m.concept}</div>}
            </td>
            <td className={`py-2 text-right font-medium ${Number(m.quantity) < 0 ? "text-rose-600" : "text-emerald-700"}`}>
              {Number(m.quantity) >= 0 ? "+" : "−"}{qty(Math.abs(Number(m.quantity)))}
            </td>
            <td className="py-2 text-right text-slate-500">{m.balance_after != null ? qty(m.balance_after) : "—"}</td>
            <td className="py-2 text-right text-slate-500">{fmtWhen(m.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ActivityDetailModal({ item, token, venueID, locations, onClose }) {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [sale, setSale] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  // El drill-down puede no estar disponible (endpoint aún no desplegado, o un STAFF sin
  // permiso de productos: /v1/stock/history exige `product_editor_privileges`). No es un
  // error que deba asustar a nadie — el modal cae al detalle del propio log.
  const [drillDownUnavailable, setDrillDownUnavailable] = useState(false);
  // Y esto es lo contrario: algo se rompió (404, 500, red, endpoint sin desplegar). No es
  // cosa de los permisos del usuario, y decírselo lo manda a buscar donde no está.
  const [drillDownError, setDrillDownError] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Cerrar con Escape: el modal se abre desde una fila de tabla y hasta ahora solo se podía
  // cerrar con el ratón.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const pairs = useMemo(
    () => formatActivityDetails(item.action, item.details, locations),
    [item.action, item.details, locations]
  );

  const load = useCallback(async () => {
    if (!item.entity_id) return;
    setLoading(true);
    setDrillDownUnavailable(false);
    setDrillDownError(false);
    try {
      if (item.entity_type === "transaction") {
        const res = await apiFetch(`/v1/transactions/${item.entity_id}`, { headers: authHeaders });
        if (!res.ok) throw new Error(String(res.status));
        setSale(await res.json());
      } else if (item.entity_type === "product") {
        const res = await apiFetch(
          `/v1/stock/history?product_id=${item.entity_id}&venue_id=${venueID}&limit=10`,
          { headers: authHeaders }
        );
        if (!res.ok) throw new Error(String(res.status));
        setHistory(await res.json());
      }
    } catch (err) {
      // Un 403 SÍ es "tu usuario no puede". Cualquier otra cosa (404, 500, red caída, el
      // endpoint aún sin desplegar) no lo es: decírselo a un OWNER es mentirle sobre sus
      // permisos y mandarlo a buscar el problema donde no está.
      if (String(err.message) === "403") setDrillDownUnavailable(true);
      else setDrillDownError(true);
    } finally {
      setLoading(false);
    }
  }, [item.entity_id, item.entity_type, authHeaders, venueID]);

  useEffect(() => { load(); }, [load]);

  // Sin `entity_id` no hay nada que cargar: si no se contempla aquí, el modal se queda con
  // la cabecera y ningún detalle, sin decir por qué.
  const hasDrillDown =
    Boolean(item.entity_id) &&
    (item.entity_type === "transaction" || item.entity_type === "product");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" {...overlayDismiss}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
       
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-bold text-slate-800">{item.summary || "Detalle"}</h2>
          <button
            onClick={onClose}
            className="text-2xl leading-none text-slate-400 hover:text-slate-700"
            aria-label="Cerrar"
          >×</button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <Meta item={item} />

          {pairs.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-slate-50 p-3 text-sm">
              {pairs.map((p) => (
                <React.Fragment key={p.label}>
                  <dt className="text-slate-400">{p.label}</dt>
                  <dd className="text-slate-700">{p.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}

          {loading && <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>}

          {!loading && sale && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Qué se vendió</h3>
              <SaleDetail sale={sale} />
            </section>
          )}

          {!loading && history && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Últimos movimientos del producto
              </h3>
              <ProductHistory history={history} />
            </section>
          )}

          {!loading && hasDrillDown && drillDownUnavailable && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
              El detalle ampliado no está disponible para tu usuario.
            </p>
          )}

          {!loading && hasDrillDown && drillDownError && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              No se pudo cargar el detalle ampliado. Inténtalo de nuevo.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
